import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Footer from './Footer';

interface MonitoringPageProps {
  t: any;
  isRTL: boolean;
}

const MonitoringContainer = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: white;
  padding: 2rem 2rem 0 2rem;
  padding-top: 140px;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  
  @media (max-width: 768px) {
    padding: 1rem 1rem 0 1rem;
    padding-top: 100px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div<{ $isRTL: boolean }>`
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const MainTitle = styled.h1<{ $isRTL: boolean }>`
  font-size: 2.5rem;
  color: #1f2937;
  margin: 0 0 1rem 0;
  font-weight: 700;
  line-height: 1.2;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p<{ $isRTL: boolean }>`
  font-size: 1.3rem;
  color: #1f2937;
  margin: 0;
  font-weight: 400;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ContentSection = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 16px;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Paragraph = styled.p<{ $isRTL: boolean }>`
  font-size: 1.05rem;
  color: #374151;
  line-height: 1.8;
  margin: 0;
  text-align: justify;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  
  strong {
    color: #1f2937;
    font-weight: 600;
  }
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const HighlightText = styled.span`
  color: #dc2626;
  font-weight: 600;
`;

const ProviderSection = styled.div<{ $isRTL: boolean }>`
  background: white;
  border: 2px solid #d1d5db;
  border-radius: 16px;
  padding: 2rem;
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 1.5rem;
    text-align: center;
  }
`;

const ProviderText = styled.p<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  color: #1f2937;
  margin: 0;
  font-weight: 500;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ProviderLogo = styled.img`
  max-width: 150px;
  height: auto;
  object-fit: contain;
  
  @media (max-width: 768px) {
    max-width: 120px;
  }
`;

const MonitoringPage: React.FC<MonitoringPageProps> = ({ t, isRTL }) => {
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <MonitoringContainer $isRTL={isRTL}>
        <Container>
          <Header $isRTL={isRTL}>
            <MainTitle $isRTL={isRTL}>{t.monitoringPageTitle}</MainTitle>
            <Subtitle $isRTL={isRTL}>{t.monitoringPageSubtitle}</Subtitle>
          </Header>

          <ContentSection
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <ContentGrid>
              <Column>
                <Paragraph $isRTL={isRTL}>
                  <strong>{t.techyid}</strong> {t.monitoringPageContent1}
                </Paragraph>
              </Column>

              <Column>
                <Paragraph $isRTL={isRTL}>
                  {t.monitoringPageContent2}
                </Paragraph>
              </Column>
            </ContentGrid>

            <ProviderSection $isRTL={isRTL}>
              <ProviderText $isRTL={isRTL}>
                {t.monitoringPageProviderText}
              </ProviderText>
              <ProviderLogo
                src="/images/partners/cahors.png"
                alt={t.monitoringPageCahorsAlt}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/images/logo.png';
                }}
              />
            </ProviderSection>
          </ContentSection>
        </Container>
      </MonitoringContainer>
      <Footer t={t} isRTL={isRTL} />
    </>
  );
};

export default MonitoringPage; 